import React, {ChangeEvent, lazy, ReactElement, Suspense, SyntheticEvent, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import './DocumentEditor.less';
import Alert from '@/lib/alert';
import Document from "@/entities/document";
import {CURRENT_DOCUMENT_PROTOCOL, downloadLocal, downloadRemote, fetchV1RemoteContent, getAsString, getDropFiles, getFileName, getFileType, isLikelyBase64, normalizeDocumentBuffer, readFileIntoDocument} from './documentHelper';
import {refuseOversizedFile} from './uploadLimits';
import FormElement from "@/lib/FormElement";
import IconButton from '@/ui/IconButton/IconButton';
import Icons from '@/ui/IconButton/Icons';
import OkCancel from "@/ui/Buttons/OkCancel";
import type {RootState} from "@/@types/store";
import {STYLESHEET} from "@/ui/TextArea/TextArea";
import {Tooltip} from "@mui/material";
import {confirmChangesMade} from "@/ui/Confirm";
import {connect} from "react-redux";
import {getCurrentFirmId, getCurrentUserId} from "@/lib/picos/currentLogin";
import {genUUID, getZeroUUID} from "@/lib/picos/uuids";
import {isDemo} from "@/lib/demo";
import {isTouchDevice} from "@/lib/picos/browserFns";
import {md5hash} from "@/lib/picos/hash";
import TextField from "@/ui/TextField";
import {uuid} from "@/@types/schema";
import newHRTimestamp from "@/lib/picos/timestamp";
import {ContentBlockChildProps} from "@/ui/ContentBlock/ContentBlock";
import {EditableTableCardProps} from "@/ui/EditableTable/EditableTable";
import useComponentCache from "@/hooks/useComponentCache";

const EntityMeta = lazy(() => import('@/ui/EntityMeta/EntityMeta'));
const Rfc822Email = lazy(() => import('@/ui/Rfc822Email/Rfc822Email'));
const PdfEditor = lazy(() => import('@/ui/editors/PdfEditor'));
const TextEditor = lazy(() => import('@/ui/editors/TextEditor'));
const HtmlEditor = lazy(() => import('@/ui/editors/HtmlEditor'));
const SpreadsheetEditor = lazy(() => import('@/ui/editors/SpreadsheetEditor'));

const MAX_LOCAL = isDemo() ? Number.MAX_SAFE_INTEGER : 5_120;

export type DocumentEditorProps = ContentBlockChildProps & EditableTableCardProps<Document> & Pick<RootState, 'documents'> & {
    // Parent ID of this document
    parentId?: string,
    // Opened read-only from Trash. A soft-deleted document has no live
    // file handle/URI, so "can't load the file" is expected — show the
    // saved preview without the alarming error chrome.
    trashReadOnly?: boolean,
};

type DocumentEditorState = {
    current: Document,
    documents: Document[],
    // Optional because the unmount write no longer stores the drop hand-off at
    // all (OWA-670) — a cached record legitimately has neither field.
    dropFiles?: File[],
    hasChanged: boolean,
    isDropHandled?: boolean,
    isInvalidName: boolean,
    isLoading: boolean,
    loadingMessage: string,
    loadingError: string,
    isNameChange: boolean,
    readPct: number,
}

/**
 Use the passed in matter, or create a new one
 This is a little more involved here, because if we are choosing FILES,
 Then this is really just a prototype that will be used to create
 new Documents, and the state.documents will be passed back to the onOk method.
 */
function DocumentEditor(props: DocumentEditorProps) {
    const id = props.current?.id || props.newId;
    const propsOnCancel = props.onCancel;

    // Component Cache
    const {cacheValue, cacheReset, cacheSave} = useComponentCache<DocumentEditorState>(`document-editor-${id}`);

    // TODO: I think this is part of the problem. There should be two flags, isNew and isSaved
    const isNew = !props.current || !props.current?.dbAddedOn;

    // Adopt the cached document ONLY when it is a genuine unsaved draft
    // (OWA-526 invariant A: a clean snapshot must never win over a newer live
    // entity; invariant B: a dirty draft must). Note this deliberately does NOT
    // read through cacheValue('current', …) — that is an existence check, so the
    // cached value would win whenever the field is present and this decision
    // would never run. That is exactly how the staleness bug hid (OWA-687).
    //
    // This replaces an `addedOn` freshness comparison that only mattered when two
    // users edited the same document at once. That comparison discarded the local
    // user's unsaved draft whenever the other party saved last; keeping the draft
    // matches the cache's primary purpose. Save remains last-write-wins either way.
    const savedDocument = cacheValue('current');
    const shouldUseSavedDocument = !!savedDocument && cacheValue('hasChanged', false) === true;

    const [current, setCurrent] = useState<Document>(
        (shouldUseSavedDocument
                              ? savedDocument
                              : (props.current || new Document({
                documentId: props.newId,
                firmId: getCurrentFirmId(),
                addedBy: getCurrentUserId(),
                addedOn: newHRTimestamp(),
                status: 'C',
                version: 1,
                protocolVersion: CURRENT_DOCUMENT_PROTOCOL,
                local: true,
                hash: null
            }))));

    // `uri`/`uriExpires` are transient signed URLs that the caller refreshes
    // immediately before opening the editor (Trash, DocumentBlock, etc.).
    // The cache-restored `current` above has stale/missing values for these
    // and PdfEditor renders nothing when there's no uri — sync them from
    // props.current whenever it provides a fresh signature. We clone the
    // existing Document (preserves buffer/plainText and any mid-edit
    // state) and overwrite just the two signed fields, so React sees a
    // new reference and downstream consumers (PdfEditor, the loader)
    // re-evaluate. Guarded by an equality check so this is a no-op when
    // the uri is already up to date.
    const incomingUri = (props.current as any)?.uri as string | undefined;
    const incomingUriExpires = (props.current as any)?.uriExpires as Date | string | undefined;
    useEffect(() => {
        if (!incomingUri) return;
        if ((current as any)?.uri === incomingUri) return;
        setCurrent((prev: any) => {
            if (!prev) return prev;
            const next = typeof prev.clone === 'function' ? prev.clone() : new Document({...prev});
            next.uri = incomingUri;
            next.uriExpires = incomingUriExpires;
            return next;
        });
    }, [incomingUri, incomingUriExpires, current]);
    const [documents, setDocuments] = useState<Document[]>(cacheValue('documents', null));
    const [dropFiles, setDropFiles] = useState<File[]>(cacheValue('dropFiles', (props.env ? props.env.dropFiles : null)));
    const [hasChanged, setHasChanged] = useState<boolean>(cacheValue('hasChanged', (props.env ? props.env.hasChanged : false)));
    const [isDropHandled, setIsDropHandled] = useState<boolean>(cacheValue('isDropHandled', false));
    const [isInvalidName, setIsInvalidName] = useState<boolean>(cacheValue('isInvalidName', false));
    const [isLoading, setIsLoading] = useState<boolean>(cacheValue('isLoading', false)); // Are we currently loading a remote file?
    const [isNameChange, setIsNameChange] = useState<boolean>(cacheValue('isNameChange', false)); // Are we doing a name change?
    const [loadingError, setLoadingError] = useState<string>(cacheValue('loadingError', '')); // What error occurred while loading a remote file?
    const [loadingMessage, setLoadingMessage] = useState<string>(cacheValue('loadingMessage', '')); // What is the message we are showing while loading a remote file?
    const [readPct, setReadPct] = useState<number>(cacheValue('readPct', 0)); // What percentage of the remote file has been loaded?

    const cancelRef = useRef<boolean>(false);
    const doResetRef = useRef<boolean>(false);
    const loadTimeoutRef = useRef<NodeJS.Timeout>(null);
    const processFileTimeoutRef = useRef<NodeJS.Timeout>(null);
    const upgradeRef = useRef<string>(null);
    const loadAttemptedRef = useRef<string>(null);

    // Auto-detect V1 remote documents: S3 may serve base64 text instead of binary.
    // fetchV1RemoteContent uses content-based detection (no protocolVersion check).
    useEffect(() => {
        if (!current.local && current.uri && upgradeRef.current !== current.id) {
            upgradeRef.current = current.id;
            fetchV1RemoteContent(current)
                .then(displayDoc => {
                    if (displayDoc) setCurrent(displayDoc);
                })
                .catch(err => console.error('fetchV1RemoteContent failed', err));
        }
    }, [current]);

    /**
     * Load the buffer in the background.
     */
    const loadBuffer = useCallback(async () => {
        // If we are temporarily offline, show a message
        if (!navigator.onLine) {
            setLoadingMessage('Offline, showing read-only preview');
            setLoadingError('');
            setIsLoading(false);
        }

        // Load the remote buffer into memory
        if (current.fileHandle || current.uri) {
            try {
                const uDoc = await readFileIntoDocument(current, current.fileSize < MAX_LOCAL, (pct) => {
                    setLoadingMessage(`Loaded ${pct * 100}% of the document`);
                    setReadPct(100 * pct);
                });

                uDoc.originalHash = md5hash(uDoc.buffer || uDoc.plainText);

                setLoadingMessage('Finished loading file.');
                setLoadingError('');
                setCurrent(uDoc);
            }
            catch (err: any) {
                console.error(err);
                setLoadingMessage('');
                setLoadingError(err.message || 'Could not load the document.');
            }
            finally {
                setIsLoading(false);
                setHasChanged(false);
            }
        }
        else {
            setIsLoading(false);
        }
    }, [current]);

    /**
     * The first time we load this document, we need to check if it is a remote document of type text/*.
     * If so, we need to load the contents of the remote uri into the current document's buffer.
     * We should show what is in plainText as a read-only document until we finish loading the file.
     */
    useEffect(() => {
        const iife = async () => {
            if (readPct === 0 && !isLoading && !loadingError && !current.local && loadAttemptedRef.current !== current.documentId && (current.contentType?.startsWith('text/') || current.contentType?.startsWith('message/rfc822'))) {
                loadAttemptedRef.current = current.documentId;
                setIsLoading(true);
                setLoadingMessage('Loading document...');

                await loadBuffer();

                setIsLoading(false);
                setHasChanged(false);
            }
            else {
                // Set the pre-edit baseline hash ONCE, when content first appears, via a
                // real state transition. Never mutate the state-held Document in place: the
                // same reference is written to the component cache (cacheSave({current})),
                // and recomputing originalHash after edits land would clobber the change-
                // detection baseline (store/hooks.ts: revised.hash !== doc.originalHash),
                // silently dropping saves of edited remote documents. The `== null` guard
                // makes this set-once and prevents a setCurrent loop (current is a dep).
                if (current.originalHash == null && (current.plainText || current.buffer)) {
                    const next = current.clone();
                    next.originalHash = md5hash(current.buffer || current.plainText);
                    setCurrent(next);
                }
            }
        };

        iife().catch(console.error);
    }, [current, isLoading, loadBuffer, loadingError, readPct]);

    useEffect(() => {
        return () => {
            // Read inside the cleanup, not in the effect body. The body runs once
            // at mount, so it pins whichever handle existed then; every later
            // render replaces it (see the dropFiles block below) and the newest
            // timer would survive unmount and fire into a dead component.
            const loadTimeout = loadTimeoutRef.current;
            const processFileTimeout = processFileTimeoutRef.current;

            if (loadTimeout) clearTimeout(loadTimeout);
            if (processFileTimeout) clearTimeout(processFileTimeout);

            loadTimeoutRef.current = null;
            processFileTimeoutRef.current = null;
        }
    }, []);

    // useEffect(() => {
    //     function clearChildren(element: any) {
    //         element.setAttribute && element.setAttribute('tabindex', -1);
    //
    //         Array.from(element.childNodes).forEach(cn => {
    //             clearChildren(cn);
    //         });
    //     }
    //
    //     Array.from(window.document.getElementsByClassName('ql-toolbar'))
    //         .forEach(tb => clearChildren(tb));
    // }, []);

    // Handle newly dropped files
    useEffect(() => {
        if (!!props.env?.dropFiles && !dropFiles && !isDropHandled) {
            setDropFiles(props.env?.dropFiles);
            setIsDropHandled(true);
        }
    }, [dropFiles, isDropHandled, props.env?.dropFiles]);

    // Save cache
    useLayoutEffect(() => {
        return () => {
            if (doResetRef.current) cacheReset();
            else {
                // Gate-write the entity (OWA-526 rule, as already applied to
                // InvoiceCard for the May 2026 Lawson bug). Cache `current` ONLY
                // when the user has unsaved edits.
                //
                // Caching it unconditionally broke invariant A (no-stale): the
                // clean snapshot won over a newer live entity on the next mount,
                // because cacheValue() is an existence check — so the
                // shouldUseSavedDocument freshness comparison above never ran.
                // Viewing a document, leaving, and returning after another tab or
                // a sync updated it showed the stale copy (OWA-687).
                //
                // It also parked the whole document buffer in memory for every
                // document merely viewed. Nothing is lost by omitting it: crash,
                // reload and close are covered by the IndexedDB autosave store,
                // and a genuinely dirty draft is still cached below.
                // `dropFiles` and `isDropHandled` are deliberately NOT cached.
                // They describe one transient hand-off: DocumentBlock owns the
                // drop, passes it in through `env`, and nulls its own copy once
                // the files are processed. Caching them here pinned the dropped
                // File/Document handles — and everything they reference — for the
                // life of the tab, long after the drop was handled.
                //
                // Nothing is lost: `dropFiles` initialises from `props.env`, which
                // the parent still supplies (and itself caches) while a drop is
                // genuinely pending. Dropping `isDropHandled` with it also frees a
                // remounted editor to accept a NEW drop; a cached `true` used to
                // make the effect below ignore one.
                const rest = {documents, hasChanged, isInvalidName, isLoading, isNameChange, loadingError, loadingMessage, readPct};
                if (hasChanged) cacheSave({...rest, current});
                else cacheSave(rest as DocumentEditorState);
            }
        }
    }, [cacheReset, cacheSave, current, documents, hasChanged, isInvalidName, isLoading, isNameChange, loadingError, loadingMessage, readPct])

    /**
     * Convert a plain text note to an HTML note
     */
    const convertToFancy = useCallback(() => {
        const doc = current.clone();
        doc.contentType = 'text/html';
        doc.buffer = doc.plainText;
        setCurrent(doc);
    }, [current, setCurrent]);

    const callStorageWorker = useCallback((doc: Document): void => {
        if (!doc.id) doc.documentId = genUUID();

        if (!cancelRef.current) {
            window.storageWorker
                .cacheItem(
                    getCurrentFirmId(),
                    getCurrentUserId(),
                    'document',
                    // OWA-958: the same parent its removal uses (an editor opened without a
                    // parent writes and removes under the zero parent).
                    props.parentId ?? getZeroUUID(),
                    doc.id,
                    doc
                )
                .catch(console.error);
        }
    }, [props.parentId]);

    /**
     * Update current with changed field value.
     *
     * @param e
     * @param {string} field - The name of the field to alter in current
     * @param {string} [textOverride] - if available, text to override the value in the event
     */
    const handleTextChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: string, textOverride?: string): void => {
        e.preventDefault();

        const doc = current.clone();
        doc[field] = textOverride || e.target.value;

        if (!doc.fileHandle) doc.fileSize = doc[field].length;
        doc.addedOn = newHRTimestamp();
        doc.addedBy = getCurrentUserId();

        // Update fields for auto-save
        if (!doc.contentType) {
            if (props.param === 'html') doc.contentType = 'text/html';
            else if (props.param === 'plain') doc.contentType = 'text/plain';
            doc.entityId = props.parentId;
        }

        if (['text/html', 'text/plain'].includes(doc.contentType)) {
            callStorageWorker(doc);
        }

        setCurrent(doc);
        setHasChanged(true);
    }, [callStorageWorker, current, props.param, props.parentId]);

    const processFiles = useCallback((files: File[]) => {
        // ISSUE: The javascript is so fast that we are getting conflicting timestamps.
        // This means that IDB will reject some of them, as they have the same addedOn value.
        // We thus make them sequential instead of real timestamps
        const timestamp = newHRTimestamp();

        const docs = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // OWA-1149: a file past its limit is refused here, before it becomes a document.
            const contentType = file.type || getFileType(file.name);
            if (refuseOversizedFile(file.name, file.size, contentType)) continue;

            let doc: Document;

            if (isNew) {
                doc = new Document({
                    documentId: genUUID(),
                    firmId: getCurrentFirmId(),
                    addedBy: getCurrentUserId(),
                    addedOn: timestamp + i,
                    status: 'C',
                    version: 1,
                    protocolVersion: CURRENT_DOCUMENT_PROTOCOL,
                    local: true,
                    originalName: file.name
                });
            }
            else doc = current.clone();

            doc.fileName = file.name;
            doc.fileSize = file.size;
            // doc.lastModified = file.lastModified ? new Date(file.lastModified) : new Date();
            doc.contentType = contentType;
            doc.fileHandle = file;

            // If we had previously renamed this, we need to wipe the rename out.
            doc.nameChange = false;
            doc.newFileName = '';
            doc.plainText = '';

            docs.push(doc);

            // For plain text files, we can just read in the text.
            if (file.type === 'text/plain') {
                readFileIntoDocument(doc, doc.fileSize < MAX_LOCAL)
                    .then(uDoc => {
                        // Update the state with this doc's updated info
                        setDocuments(previous => {
                            const index = previous?.findIndex((d: Document) => d.id === uDoc.id) ?? -1;
                            if (index < 0) return previous;
                            const updated = [...previous];
                            updated[index] = uDoc;
                            return updated;
                        });
                    });
            }
        }

        // Every file was refused: nothing changed.
        if (docs.length === 0) {
            setDropFiles(null);
            return;
        }

        const _documents = documents ? [...documents, ...docs] : docs;

        setDocuments(_documents);
        setHasChanged(true);
        setDropFiles(null);
    }, [current, documents, isNew, setDocuments]);

    const onFilePickerChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        const files = [];
        const inputFiles = (window.document.getElementById('documentInput') as HTMLInputElement).files;

        for (let i = 0; i < inputFiles.length; i++) {
            files.push(inputFiles.item(i));
        }

        processFiles(files);
    }, [processFiles]);

    const onDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (e.dataTransfer && e.dataTransfer.items) {

            for (let i = 0; i < e.dataTransfer.items.length; i++) {
                const dt = e.dataTransfer.items[i];

                if (dt.kind === 'string') {
                    getAsString(dt)
                        .catch(err => console.warn(err));
                }
            }
        }
    }, []);

    const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        // Prevent default behavior (Prevent file from being opened)
        e.preventDefault();

        getDropFiles(e)
            .then(({dropFiles}) => {
                if (dropFiles.length > 0) {
                    processFiles(dropFiles);
                }
            })
            .catch(err => {
                Alert.add(new Alert(Alert.ERROR, getZeroUUID(), 0, err.message));
                console.error(err);
            });
    }, [processFiles]);

    /**
     * Cancel this form
     */
    const onCancel = useCallback(async (e: SyntheticEvent) => {
        e.preventDefault();

        const _doWork = async () => {
            doResetRef.current = true;
            cancelRef.current = true;

            // Clear out anything in storageWorker
            const parentId = props.parentId ?? getZeroUUID();

            await window.storageWorker
                .uncacheItem(
                    getCurrentFirmId(),
                    getCurrentUserId(),
                    'document',
                    parentId,
                    current.id
                )
                .catch((err: any) => {
                    Alert.add(new Alert(Alert.ERROR, getZeroUUID(), 0, err.message));
                    console.error(err)
                })
                .finally(() => {
                    propsOnCancel();
                });
        };

        if (hasChanged) {
            // Ask if they really want to save
            confirmChangesMade(_doWork);
        }
        else {
            await _doWork();
        }
    }, [current.id, hasChanged, props.parentId, propsOnCancel]);

    /**
     * Prepare the document to be sent back to the caller.
     *
     * We will remove this document from the cache once we are SURE it has been saved.
     *
     * @param {Event} e
     */
    const onOk = useCallback(async (e: SyntheticEvent) => {
        e.preventDefault();

        // Most callers return void. The failed-document correction dialog
        // returns false when its queue write fails; keep the draft and File in
        // this editor until it has a durable copy.
        const submit = async (document: Document | Document[]): Promise<boolean> => {
            const accepted = await (props.onOk as (value: Document | Document[]) => boolean | void | Promise<boolean | void>)(document);
            if (accepted === false) return false;
            doResetRef.current = true;
            cacheReset();
            return true;
        };

        const _uncacheDocument = async (document: Document): Promise<Document> => {
            document.waitingToSave = false;

            const parentId = props.parentId ?? getZeroUUID();

            // Call autosave
            await window.storageWorker
                .uncacheItem(
                    getCurrentFirmId(),
                    getCurrentUserId(),
                    'document',
                    parentId,
                    document.id,
                )
                .catch((err: any) => {
                    Alert.add(new Alert(Alert.ERROR, getZeroUUID(), 0, err.message));
                    console.error(err)
                });

            return document;
        }

        const timestamp = newHRTimestamp();

        // If we are processing new documents
        if (documents && isNew) {
            // Clone before mutating: [...documents] copies the array but not its elements,
            // so the loop below would mutate the Document instances still held in `documents`
            // state (and escape them to the parent/Redux via props.onOk). Clone each instead.
            const docs = documents.map(d => d.clone());

            for (let i = 0; i < docs.length; i++) {
                const document = docs[i];

                // Set common properties
                document.addedBy = getCurrentUserId();
                document.addedOn = timestamp + i;
                document.status = 'C';
                // document.fileSize = document.fileHandle.size;
                document.local = document.fileHandle.size < MAX_LOCAL;  // 5k, for now
                // document.contentType = document.fileHandle.type || getFileType(document.fileName);
                // document.version needs to be calculated once the file has been readD
            }

            if (!await submit(docs.length === 1 ? docs[0] : docs)) return;
        }

        // If we are updating multiple documents (is this ever called? only time we have multiple docs is onDrop)
        else if (documents && !isNew) {
            // Clone before mutating — documents[0] is the live first element of the
            // `documents` state array; mutating it in place corrupts state and escapes to
            // the parent/Redux via props.onOk. Matches the nameChange/text branches below.
            const document = documents[0].clone();

            // Set common properties
            document.addedBy = getCurrentUserId();
            document.addedOn = newHRTimestamp();
            document.status = 'U';
            document.fileSize = document.fileHandle.size;
            document.local = document.fileHandle.size < MAX_LOCAL;  // 5k, for now
            document.contentType = document.fileHandle.type || getFileType(document.fileName);
            document.version = document.version + 1;

            if (!await submit(document)) return;
        }

        // Processing a nameChange
        else if (current && isNameChange) {
            const document = current.clone();

            if (document.dbAddedOn) {
                document.nameChange = true;
            }

            if (!await submit(document)) return;
            await _uncacheDocument(document)
                .catch(err => {
                    Alert.add(new Alert(Alert.ERROR, getZeroUUID(), 0, err.message));
                    console.error(err)
                });
        }

        // Otherwise processing a text change
        else {
            // Update common fields
            let document = current.clone();

            if (!(document.buffer || document.plainText)) document.plainText = ' ';

            // Set common properties
            document.addedBy = getCurrentUserId();
            document.addedOn = newHRTimestamp();
            document.status = isNew ? 'C' : 'U';
            document.local = (document.buffer || document.plainText).length < MAX_LOCAL; // <5k, for now
            document.fileSize = (document.buffer || document.plainText).length || 0;
            document.contentType = document.buffer ? 'text/html' : 'text/plain';

            if (!await submit(document)) return;
            await _uncacheDocument(document)
                .catch(err => {
                    Alert.add(new Alert(Alert.ERROR, document?.id, document?.addedOn, err.message));
                    console.error(err);
                });
        }

        doResetRef.current = true;
    }, [cacheReset, current, documents, isNameChange, isNew, props]);

    const handleBufferChange = useCallback((content: string, text: string) => {
        if (content === (current?.buffer || current?.plainText)) return;

        const doc = current.clone();
        doc.buffer = content;
        doc.plainText = text;
        doc.addedOn = newHRTimestamp();

        if (!doc.contentType) {
            if (props.param === 'html') doc.contentType = 'text/html';
            else if (props.param === 'plain') doc.contentType = 'text/plain';
            doc.entityId = props.parentId;
        }

        if (['text/html', 'text/plain'].includes(doc.contentType)) {
            callStorageWorker(doc);
        }

        setCurrent(doc);
        setHasChanged(true);
    }, [callStorageWorker, current, props.param, props.parentId]);

    /**
     * Handle key presses
     * @param {React.KeyboardEvent<HTMLInputElement>} e
     * @return {boolean}
     */
    const handleEnter = useCallback(async (e: React.KeyboardEvent<HTMLInputElement>): Promise<boolean> => {
        const document = current;

        const enabled = (
            hasChanged &&
            (documents || (document.buffer && document.buffer.length > 0) || (document.plainText && document.plainText.length > 0))
        );

        if (e.key === 'Enter') {
            if (enabled) {
                await onOk(e);
            }
            else {
                e.preventDefault();
                e.stopPropagation();
            }
        }
        else return true;
    }, [current, documents, hasChanged, onOk]);

    /**
     * Always focus all text in the title field
     */
    const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        if (!current.fileName || current.fileName === 'Untitled')
            event.target.select();
    }, [current.fileName]);

    const changeFileName = useCallback((e: ChangeEvent<HTMLInputElement>) => handleTextChange(e, 'fileName'), [handleTextChange]);

    /** String-based onChange handler for TextEditor component */
    const handlePlainTextChange = useCallback((content: string) => {
        const doc = current.clone();
        doc.plainText = content;
        if (!doc.fileHandle) doc.fileSize = content.length;
        doc.addedOn = newHRTimestamp();
        doc.addedBy = getCurrentUserId();
        if (!doc.contentType) {
            if (props.param === 'html') doc.contentType = 'text/html';
            else if (props.param === 'plain') doc.contentType = 'text/plain';
            doc.entityId = props.parentId;
        }
        if (['text/html', 'text/plain'].includes(doc.contentType)) {
            callStorageWorker(doc);
        }
        setCurrent(doc);
        setHasChanged(true);
    }, [callStorageWorker, current, props.param, props.parentId]);

    const getSvgIntrinsicSize = (svgString: string): {width: string, height: string} => {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
        const svgElement = svgDoc.documentElement;

        const widthAttr = svgElement.getAttribute("width");
        const heightAttr = svgElement.getAttribute("height");
        const viewBoxAttr = svgElement.getAttribute("viewBox");

        let width = null;
        let height = null;

        if (widthAttr && heightAttr) {
            width = parseFloat(widthAttr);
            height = parseFloat(heightAttr);
        } else if (viewBoxAttr) {
            const viewBoxValues = viewBoxAttr.split(" ").map(Number);
            if (viewBoxValues.length === 4) {
                width = viewBoxValues[2];
                height = viewBoxValues[3];
            }
        }

        return { width, height };
    };

    /**
     * Render the image linked to by the document.
     *
     * @param {Document} doc
     * @returns {ReactElement}
     */
    const renderImage = useCallback((doc: Document): ReactElement => {
        let image: ReactElement;

        if (doc.uri) image = <Tooltip title={getFileName(doc)}><img alt={getFileName(doc)} src={doc.uri}/></Tooltip>;
        else {
            // Have to package up
            const contents = doc.buffer || doc.plainText || '';
            let w = null;
            let h = null;

            if (doc.fileName.endsWith('.svg')) {
                try {
                    const svgText = isLikelyBase64(contents) ? window.atob(contents) : contents;
                    const sizes = getSvgIntrinsicSize(svgText);
                    w = sizes.width;
                    h = sizes.height;
                }
                catch (e) {
                    console.warn('Could not parse SVG dimensions', e);
                }
            }

            const base64 = normalizeDocumentBuffer(contents, doc.contentType);
            const src = `data:${doc.contentType};base64,${base64}`;

            image = <Tooltip title={getFileName(doc)}>
                <img alt={getFileName(doc)}
                     src={src}
                     height={h}
                     width={w}
                />
            </Tooltip>;
        }

        return <div className='row image'>{image}</div>;
    }, []);

    /**
     * This is shown when the editor is opened as type 'plain' or 'html'.
     *
     * If this document is local, we are done.
     * If this document is remote, check if the buffer has been loaded.
     *   - If loaded, we are done.
     *   - If not loaded, we need to load it (async, of course!)
     *
     * To load a document, we need to make an async call to load the file into the buffer, then change the state.
     *
     * @param {string} param - plain or html
     * @param {FormElement} element
     * @returns {ReactElement}
     */
    const renderEditor = useCallback((param: string, _element: FormElement): ReactElement => {
        const doc = current;
        const isMail = doc.contentType === 'message/rfc822';

        // Determine if content is loaded based on content type
        const plainLoaded = (doc.contentType === 'text/plain') && (doc.local || !!doc.buffer || (readPct === 100));
        const htmlLoaded = (doc.contentType === 'text/html' || isMail) && !!(doc.local || !!doc.buffer || (readPct === 100));

        // Determine content to display (used for loading/error states and email)
        const getContent = (): string => {
            if (doc.contentType === 'application/pdf') {
                return doc.buffer || '';
            }
            if (isMail && readPct === 100) {
                return doc.local ? doc.plainText : doc.buffer;
            }
            if (doc.local || isNew || (doc.contentType === 'text/plain' && plainLoaded) || (doc.contentType === 'text/html' && htmlLoaded)) {
                return doc.buffer || doc.plainText || '';
            }
            if (!isNew && !doc.local && ((doc.contentType === 'text/plain' && !plainLoaded) ||
                (doc.contentType === 'text/html' && !htmlLoaded) ||
                (isMail && !htmlLoaded)
            )) {
                return doc.plainText || '';
            }
            console.error('Fell out of logic loop');
            return '';
        };

        // Error state
        if (loadingError) {
            const content = getContent();
            return (
                <div className='document-editor__text-editor'>
                    <div className='document-editor__error-title'>There was an error loading the document.</div>
                    <div className='document-editor__error-message'>{loadingError}. Showing saved preview.</div>
                    {doc.contentType === 'text/html'
                        ? <div className='document-editor__preview'
                               style={{overflow: 'auto', maxHeight: `calc(${props.containerHeight}px - 8em)`}}
                          >{content}</div>
                        : <div className='document-editor__text-preview'
                               style={{overflow: 'auto', maxHeight: `calc(${props.containerHeight}px - 8em)`}}
                               dangerouslySetInnerHTML={TextField.createMarkup(content.replaceAll('\n', '<br/>'))}
                          />
                    }
                </div>
            );
        }

        // Loading state
        if (isLoading) {
            const content = getContent();
            return (
                <div className='document-editor__text-editor'>
                    <div className='document-editor--loading'>
                        {loadingMessage && <div>{loadingMessage}</div>}
                        <progress id='load-progress' max={100} value={readPct}/>
                    </div>
                    <pre className='document-editor__text-editor'>{content}</pre>
                </div>
            );
        }

        // RFC822 emails
        if (isMail) {
            const content = getContent();
            if (!doc.plainText && !content?.length) return null;
            return (
                <Suspense>
                    <Rfc822Email
                        documentId={doc.id}
                        message={content || doc.plainText}
                        height={props.containerHeight}
                    />
                </Suspense>
            );
        }

        // PDFs
        if (doc.contentType === 'application/pdf') {
            return (
                <Suspense>
                    <PdfEditor
                        document={doc}
                        containerWidth={props.containerWidth}
                        containerHeight={props.containerHeight}
                    />
                </Suspense>
            );
        }

        // Spreadsheets (CSV, XLS, XLSX)
        const isSpreadsheet = doc.contentType === 'text/csv'
            || doc.contentType === 'application/vnd.ms-excel'
            || doc.contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            || doc.fileName?.toLowerCase().endsWith('.csv')
            || doc.fileName?.toLowerCase().endsWith('.xls')
            || doc.fileName?.toLowerCase().endsWith('.xlsx');

        if (isSpreadsheet) {
            return (
                <Suspense>
                    <SpreadsheetEditor
                        document={doc}
                        containerWidth={props.containerWidth}
                        containerHeight={props.containerHeight}
                    />
                </Suspense>
            );
        }

        // Rich HTML
        if (doc.contentType === 'text/html' || (!doc.contentType && param === 'html')) {
            return (
                <Suspense>
                    <HtmlEditor
                        document={doc}
                        isNew={isNew}
                        onChange={handleBufferChange}
                        onFileNameChange={changeFileName}
                        onEnterKey={handleEnter}
                        onTitleFocus={handleFocus}
                    />
                </Suspense>
            );
        }

        // Plain text (default)
        return (
            <Suspense>
                <TextEditor
                    document={doc}
                    isNew={isNew}
                    onChange={handlePlainTextChange}
                    onBlur={() => callStorageWorker(doc)}
                    onFileNameChange={changeFileName}
                    onEnterKey={handleEnter}
                    onTitleFocus={handleFocus}
                    onConvertToHtml={convertToFancy}
                />
            </Suspense>
        );
    }, [callStorageWorker, changeFileName, convertToFancy, current, handleBufferChange, handleEnter, handleFocus, handlePlainTextChange, isLoading, isNew, loadingError, loadingMessage, props.containerHeight, props.containerWidth, readPct]);

    const downloadFile = useCallback(() => {
        // Prefer buffer if loaded (handles V1 docs whose buffer was populated by fetchV1RemoteContent)
        if (current.buffer) downloadLocal(getFileName(current), current.buffer, current.contentType);
        else if (current.uri) downloadRemote(getFileName(current), current.uri);
        else downloadLocal(getFileName(current), current.plainText, current.contentType);
    }, [current]);

    /**
     * @param {ChangeEvent<HTMLInputElement>} e
     * @param {uuid} docId
     */
    const onChangeName = useCallback((e: React.ChangeEvent<HTMLInputElement>, docId: uuid) => {
        const newName = e.target.value;

        const _documents = documents ? [...documents] : [];
        const index = _documents.findIndex(doc => docId === doc.id);
        const doc = _documents.find(doc => docId === doc.id);
        doc.fileName = newName;
        _documents.splice(index, 1, doc);

        setDocuments(_documents);
        setHasChanged(true);
        setIsInvalidName(!doc.fileName);
    }, [documents]);

    /**
     * Called when renaming a file.  If this file is new, then we don't have to invoke the nameChange behavior,
     * and we just change the fileName.  If this file exists, then we need to invoke nameChange.
     * @param {ChangeEvent<HTMLInputElement>} e
     */
    const renameFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        const doc = current.clone();

        if (doc.dbAddedOn) doc.newFileName = e.target.value;
        else doc.fileName = e.target.value;

        setCurrent(doc);
        setIsNameChange(true);
        setHasChanged(true);
    }, [current]);

    /**
     * Show a file picker, with a drop zone.
     *
     */
    const renderPicker = useCallback((): ReactElement => {
        const isReplacement = !isNew;

        const element = new FormElement('document-editor', current, renameFile);

        let dropContents: any;
        if (documents) dropContents = documents
            .map((doc: Document) => <div key={doc.id}>
                <input type='text' key={doc.id}
                       value={doc.fileName}
                       onChange={e => onChangeName(e, doc.id)}
                       placeholder={doc.originalName}/>
            </div>);
        else dropContents = current.fileHandle && current.fileName ? current.fileName : 'Drop File(s) Here';

        if (isReplacement) {
            // Updating an existing file.
            return (
                <div className='document-editor__picker'>
                    {/* File Picker */}
                    <div className='row'>
                        <div className='document-editor__info'>
                            <p className='document-editor__info-name'>File: {getFileName(current)}</p>

                            <p className='document-editor__info-message'>This document type does not have an inline viewer available.</p>

                            {/* Download the file */}
                            {element.renderElement('Download', 'Download',
                                <IconButton type={Icons.DOWNLOAD}
                                            title='Download'
                                            color='blue'
                                            textColor='blue'
                                            label={getFileName(current)}
                                            onClick={downloadFile}
                                />)
                            }

                            {/* Change the name */}
                            {element.renderElement('newFileName', 'Rename to',
                                <input type='text'
                                       value={getFileName(current) || ''}
                                       onChange={renameFile}
                                />
                            )}

                            {!isNameChange && element.renderElement('Replace', 'Replace',
                                <div className='document-editor__rename-file'>
                                    <input id='documentInput'
                                           type='file'
                                           multiple={false}
                                           required={true}
                                           onChange={onFilePickerChange}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        // Adding new files
        else return (
            <div className='document-editor__picker'>
                {/* File Picker */}
                <div className='row'>
                    <input id='documentInput'
                           autoFocus={!isTouchDevice()}
                           type='file'
                           multiple={true}
                           required={true}
                           onChange={onFilePickerChange}
                    />
                </div>

                {documents &&
					<div className='document-editor__drop-zone-title'>
						You can modify the document names below before they are saved
					</div>
                }

                {/* Drop Zone for Drag and Drop, and for showing current files selected */}
                <div className='document-editor__drop-zone'>
                    <div className='document-editor__drop-zone-text' onDrop={onDrop}
                         onDragEnter={onDragEnter}>{dropContents}</div>
                </div>
            </div>
        );
    }, [current, documents, downloadFile, isNameChange, isNew, onChangeName, onDragEnter, onDrop, onFilePickerChange, renameFile]);

    /**
     * If this is an uploaded file, give the vital stats on it.
     *
     * Need a way to open it/edit it (maybe in place if it is type text/plain or text/html).
     *
     * How can we update it after editing? Can we track when it is saved/closed)?
     */

    const isImage = current.contentType?.startsWith('image/');

    let isText = !isImage && (isNew && props.param !== 'file');
    if (!isNew) {
        if (current.contentType === 'text/plain') isText = true;
        else if (current.contentType === 'text/html') {
            const buf = current.buffer || current.plainText;
            if (buf?.includes(STYLESHEET)) isText = true;
            isText = true;
        }
    }

    const disabled = !(
        hasChanged &&
        (
            !isInvalidName ||
            isNameChange ||
            documents ||
            (current.buffer && current.buffer?.length > 0)
            || (current.plainText && current.plainText?.length > 0)
        )
    );

    const buttonBar = (props.onOk && propsOnCancel)
                      ? <OkCancel label={isText ? 'Note' : 'Attachment'}
                                  disabled={!!disabled}
                                  onClick={onOk}
                                  onCancel={onCancel}
                                  nudgeY={-3}
                      />
                      : null;

    const element = new FormElement('document-editor', current, handleTextChange);

    let showBlock: ReactElement;

    const isMail = current.contentType === 'message/rfc822'; // || document.contentType === 'application/vnd.ms-outlook';

    // If this is a new file, there will be a param property of file, plain or html.
    // If no param is present, we need to open the file as either text or html, based on the content type,
    // OR we need to recognize this as a file, and ask the user to update its contents

    // Check if this is a spreadsheet
    const isSpreadsheet = current.contentType === 'text/csv'
        || current.contentType === 'application/vnd.ms-excel'
        || current.contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        || current.fileName?.toLowerCase().endsWith('.csv')
        || current.fileName?.toLowerCase().endsWith('.xls')
        || current.fileName?.toLowerCase().endsWith('.xlsx');

    // Handle PDFs, spreadsheets, and other viewable formats
    if (!isNew && (isText || current.contentType === 'application/pdf' || isSpreadsheet || isMail))
        showBlock = renderEditor(props.param, element);

    // Handle images
    else if (isImage) {
        showBlock = renderImage(current);
    }

    // If we are attaching file(s), show the picker
    else if ((isNew && props.param === 'file') || !isText) {
        showBlock = renderPicker();
    }

    // If this is brand spanking new, we need to ask what to create
    else if (isNew) {
        showBlock = renderEditor(props.param, element);
    }

    // If we are dropping files, and not already in the attached files section...
    if (dropFiles) {
        // Terminate the render (return null), then trigger the onDropEvent.
        // Replace rather than stack: this runs on every render while dropFiles
        // is set, and only the most recent handle is reachable for cleanup, so
        // any earlier one would survive unmount and fire into a dead component.
        if (processFileTimeoutRef.current) clearTimeout(processFileTimeoutRef.current);
        processFileTimeoutRef.current = setTimeout(processFiles, 10, dropFiles);
    }

    return (
        <div className='document-editor'>
            {/* The UI for the disposition of the document */}
            {showBlock}

            {/* Ok/Cancel */}
            {buttonBar}

            {/* Blame Log */}
            {!isNew && <Suspense>
                <EntityMeta entityType="document"
                            entity={current}
                            onVersionChange={(e) => setCurrent(e as Document)}
                />
            </Suspense>}
        </div>
    );
}

const mapStateToProps = (state: RootState) => {
    return {
        documents: state.documents
    };
};

export default connect(mapStateToProps, null)(DocumentEditor);
